const shouldIncludeValue = (key: string, val: any): boolean => {
  const allowedNumericKeys = ['isdcode'];
  const isIdKey = (key: string): boolean => /id$/i.test(key);
  const isAllowedNumber = allowedNumericKeys.includes(key.toLowerCase());
  return (
    !isIdKey(key) &&
    val !== null &&
    val !== undefined &&
    (typeof val !== 'number' || isAllowedNumber)
  );
};

const extractLabeledValues = (obj: any, collected: string[] = []): string[] => {
  Object.entries(obj).forEach(([key, val]) => {
    if (typeof val === 'object' && val !== null) {
      extractLabeledValues(val, collected);
    } else if (shouldIncludeValue(key, val)) {
      collected.push(`${key}: ${String(val)}`);
    }
  });
  return collected;
};

const flattenNewValueWithLabels = (value: string): string => {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) {
      return String(parsed);
    }
    const labeledValues = extractLabeledValues(parsed);
    return labeledValues.join(', ');
  } catch (e) {
    return value; // fallback to raw string
  }
};

const flattenNewValueWithLabelsAddress = (value: string): string => {
  try {
    const parsed = JSON.parse(value);

    const selected: string[] = [];

    if (parsed.Street1) selected.push(`Street1: ${parsed.Street1}`);
    if (parsed.Street2) selected.push(`Street2: ${parsed.Street2}`);
    if (parsed.Street3) selected.push(`Street3: ${parsed.Street3}`);
    if (parsed.City) selected.push(`City: ${parsed.City}`);
    if (parsed.StateProvince?.Name)
      selected.push(`State/Province: ${parsed.StateProvince.Name}`);
    if (parsed.ZipPostalCode)
      selected.push(`Zip/Postal Code: ${parsed.ZipPostalCode}`);
    if (parsed.Country?.Name) selected.push(`Country: ${parsed.Country.Name}`);
    if (parsed.County) selected.push(`County: ${parsed.County}`);
    console.log('selected', selected);
    return selected.join('\n');
  } catch (e) {
    return value;
  }
};

const flattenPhoneDetails = (value: string): string => {
  try {
    const parsed = JSON.parse(value);

    const selected: string[] = [];

    if (parsed.PhoneType?.Description)
      selected.push(`PhoneType: ${parsed.PhoneType.Description}`);

    const isd = parsed.Country?.ISDCode || '';
    const number = parsed.Number || '';
    if (number) selected.push(`Number: ${isd ? `${isd} ` : ''}${number}`);

    if (parsed.Comments !== null && parsed.Comments !== undefined)
      selected.push(`Comments: ${parsed.Comments}`);

    if (
      parsed.OkToLeaveMessage !== null &&
      parsed.OkToLeaveMessage !== undefined
    )
      selected.push(`Ok To Leave Message: ${parsed.OkToLeaveMessage}`);

    return selected.join('\n');
  } catch (e) {
    return value;
  }
};

const flattenObject = (obj: unknown): string => {
  if (
    typeof obj !== 'object' ||
    obj === null ||
    Array.isArray(obj) ||
    typeof obj === 'number' ||
    typeof obj === 'bigint'
  ) {
    return typeof obj === 'string' ? obj : String(obj);
  }

  return Object.entries(obj as Record<string, any>)
    .filter(([key, val]) => {
      const lowerKey = key.toLowerCase();
      const isIdField = lowerKey.includes('id');
      const isBirthdateRaw = lowerKey === 'birthdateraw';
      const isEmptyArray = Array.isArray(val) && val.length === 0;
      return !isIdField && !isBirthdateRaw && !isEmptyArray;
    })
    .map(([key, val]) => {
      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);

      if (typeof val === 'object' && val !== null) {
        const nested = val as { Name?: string };
        return `${formattedKey}: ${nested.Name ?? ''}`;
      }

      return `${formattedKey}: ${typeof val === 'string' ? val : String(val)}`;
    })
    .join('\n');
};

const tryParse = (val: any): any => {
  try {
    if (typeof val === 'string') {
      const temp = /^\d{15,}$/.test(val);
      return temp ? val : JSON.parse(val);
    }
    return val;
  } catch {
    return val;
  }
};

const formatDelimitedString = (val: any): string => {
  if (typeof val === 'string' && val.includes('|')) {
    const parts = val
      .split('|')
      .map(s => s.trim())
      .filter(Boolean);
    return parts.length > 1 ? parts.join(', ') : parts[0];
  }
  return String(val);
};

export {
  shouldIncludeValue,
  extractLabeledValues,
  flattenNewValueWithLabels,
  flattenNewValueWithLabelsAddress,
  flattenPhoneDetails,
  flattenObject,
  tryParse,
  formatDelimitedString,
};
